import {
  FETCH_COMPANIES, FETCH_COMPANIES_SUCCESS, FETCH_COMPANIES_FAILED,
  ADD_COMPANY, UPDATE_COMPANY, DELETE_COMPANY,
  FETCH_PRODUCTS, FETCH_PRODUCTS_SUCCESS, FETCH_PRODUCTS_FAILED,
  ADD_PRODUCT, UPDATE_PRODUCT, DELETE_PRODUCT,
  FETCH_COMPANY_STATES, FETCH_COMPANY_STATES_SUCCESS, FETCH_COMPANY_STATES_FAILED,
  ADD_COMPANY_STATE,
} from '../store/types';
import { firebase } from '../config/configureFirebase';
import store from '../store/store';
import { get, push, set, update, remove, onValue, off, query, ref as dbRef, orderByChild, equalTo } from 'firebase/database';
import { uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Helpers ───────────────────────────────────────────────────────────────────
async function uploadImage(storageRef, uri) {
  const resp = await fetch(uri);
  const blob = await resp.blob();
  const snap = await uploadBytesResumable(storageRef, blob);
  return getDownloadURL(snap.ref);
}

// ── Companies ─────────────────────────────────────────────────────────────────
export const fetchUserCompanies = () => (dispatch) => {
  const uid = store.getState().auth.profile?.uid;
  if (!uid) return;
  const { userCompaniesRef } = firebase;

  dispatch({ type: FETCH_COMPANIES, payload: null });
  off(userCompaniesRef(uid));
  onValue(userCompaniesRef(uid), (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const list = Object.keys(data).map(k => ({ id: k, ...data[k] }));
      dispatch({ type: FETCH_COMPANIES_SUCCESS, payload: list });
    } else {
      dispatch({ type: FETCH_COMPANIES_SUCCESS, payload: [] });
    }
  }, (err) => {
    dispatch({ type: FETCH_COMPANIES_FAILED, payload: err.message });
  });
};

export const stopFetchUserCompanies = () => () => {
  const uid = store.getState().auth.profile?.uid;
  if (!uid) return;
  const { userCompaniesRef } = firebase;
  off(userCompaniesRef(uid));
};

export const createCompany = (companyData, imageUris) => async (dispatch) => {
  try {
    const uid = store.getState().auth.profile?.uid;
    const { companiesRef, companyImageRef, lugaresDescubreRef } = firebase;
    const newRef = push(companiesRef);
    const id = newRef.key;

    // Upload images
    const imageUrls = [];
    for (let i = 0; i < (imageUris || []).length; i++) {
      if (imageUris[i]) {
        const url = await uploadImage(companyImageRef(id, i), imageUris[i]);
        imageUrls.push(url);
      }
    }

    // Create lugar_descubre entry (hidden until admin approves)
    const lugarRef = push(lugaresDescubreRef);
    const lugarKey = lugarRef.key;
    const now = Date.now();
    const lugar = {
      id:           id,
      name:         companyData.name         || '',
      category:     companyData.category     || '',
      type:         companyData.categoryKey  || '',
      description:  companyData.description  || '',
      address:      companyData.location?.address || companyData.address || '',
      lat:          companyData.location?.lat || 0,
      lng:          companyData.location?.lng || 0,
      city:         companyData.city         || '',
      country:      companyData.country      || '',
      phone:        companyData.phone        || '',
      website:      companyData.website      || '',
      openingHours: companyData.openingHours || '',
      imageUrl:     imageUrls[0]             || '',
      source:       'app',
      companyId:    id,
      approved:     false,
      createdAt:    now,
      updatedAt:    now,
    };
    await set(lugarRef, lugar);

    const company = {
      ...companyData,
      id,
      ownerId:   uid,
      images:    imageUrls,
      status:    'pending',
      lugarKey,
      createdAt: now,
      updatedAt: now,
      visits:    0,
      products:  0,
    };
    await set(newRef, company);
    dispatch({ type: ADD_COMPANY, payload: company });
    return { success: true, id };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const updateCompany = (id, companyData, newImageUris) => async (dispatch) => {
  try {
    const { singleCompanyRef, companyImageRef } = firebase;
    const updates = { ...companyData, updatedAt: Date.now() };

    if (newImageUris) {
      const imageUrls = [];
      for (let i = 0; i < newImageUris.length; i++) {
        if (newImageUris[i]?.startsWith('http')) {
          imageUrls.push(newImageUris[i]);
        } else if (newImageUris[i]) {
          const url = await uploadImage(companyImageRef(id, i), newImageUris[i]);
          imageUrls.push(url);
        }
      }
      updates.images = imageUrls;
    }

    await update(singleCompanyRef(id), updates);
    dispatch({ type: UPDATE_COMPANY, payload: { id, ...updates } });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const deleteCompany = (id) => async (dispatch) => {
  try {
    const { singleCompanyRef } = firebase;
    await remove(singleCompanyRef(id));
    dispatch({ type: DELETE_COMPANY, payload: id });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// Admin: fetch all companies
export const fetchAllCompanies = () => (dispatch) => {
  const { companiesRef } = firebase;
  dispatch({ type: FETCH_COMPANIES, payload: null });
  off(companiesRef);
  onValue(companiesRef, (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const list = Object.keys(data).map(k => ({ id: k, ...data[k] }));
      dispatch({ type: FETCH_COMPANIES_SUCCESS, payload: list });
    } else {
      dispatch({ type: FETCH_COMPANIES_SUCCESS, payload: [] });
    }
  }, (err) => {
    dispatch({ type: FETCH_COMPANIES_FAILED, payload: err.message });
  });
};

export const approveCompany = (id) => async (dispatch) => {
  try {
    const { singleCompanyRef, singleLugarDescubreRef, database } = firebase;
    const now = Date.now();

    // Read company first so we have lugarKey before any writes
    const snap = await get(singleCompanyRef(id));
    const company = snap.val();
    let lugarKey = company?.lugarKey;

    await update(singleCompanyRef(id), { status: 'active', updatedAt: now });

    // Primary: use stored lugarKey
    if (lugarKey) {
      await update(singleLugarDescubreRef(lugarKey), { approved: true, updatedAt: now });
    } else if (company) {
      // Fallback 1: find existing entry by companyId
      const q = query(dbRef(database, 'lugares_descubre'), orderByChild('companyId'), equalTo(id));
      const lugarSnap = await get(q);
      if (lugarSnap.exists()) {
        const updates = {};
        lugarSnap.forEach(child => {
          updates[`lugares_descubre/${child.key}/approved`] = true;
          updates[`lugares_descubre/${child.key}/updatedAt`] = now;
          lugarKey = child.key;
        });
        await update(dbRef(database), updates);
        if (lugarKey) await update(singleCompanyRef(id), { lugarKey });
      } else {
        // Fallback 2: no entry at all — create one now from company data
        const loc = company.location || {};
        const lat = loc.lat || company.lat || 0;
        const lng = loc.lng || company.lng || 0;
        const newRef = push(dbRef(database, 'lugares_descubre'));
        lugarKey = newRef.key;
        const lugar = {
          id,
          name:         company.name         || '',
          category:     company.category     || '',
          type:         company.type         || company.categoryKey || '',
          description:  company.description  || '',
          address:      loc.address          || company.address || '',
          lat,
          lng,
          city:         company.city         || '',
          country:      company.country      || '',
          phone:        company.phone        || '',
          website:      company.website      || '',
          openingHours: company.openingHours || '',
          imageUrl:     (company.images && company.images[0]) || company.imageUrl || '',
          source:       company.source       || 'app',
          companyId:    id,
          approved:     true,
          createdAt:    now,
          updatedAt:    now,
        };
        await set(newRef, lugar);
        await update(singleCompanyRef(id), { lugarKey });
      }
    }

    dispatch({ type: UPDATE_COMPANY, payload: { id, status: 'active' } });
  } catch (err) {
    console.error('[approveCompany]', err);
    throw err;
  }
};

export const rejectCompany = (id) => async (dispatch) => {
  const { singleCompanyRef } = firebase;
  await update(singleCompanyRef(id), { status: 'rejected', updatedAt: Date.now() });
  dispatch({ type: UPDATE_COMPANY, payload: { id, status: 'rejected' } });
};

export const disapproveCompany = (id) => async (dispatch) => {
  try {
    const { singleCompanyRef, singleLugarDescubreRef, database } = firebase;
    const now = Date.now();
    const snap = await get(singleCompanyRef(id));
    const company = snap.val();
    let lugarKey = company?.lugarKey;

    await update(singleCompanyRef(id), { status: 'blocked', updatedAt: now });

    if (lugarKey) {
      await update(singleLugarDescubreRef(lugarKey), { approved: false, updatedAt: now });
    } else if (company) {
      const q = query(dbRef(database, 'lugares_descubre'), orderByChild('companyId'), equalTo(id));
      const lugarSnap = await get(q);
      if (lugarSnap.exists()) {
        const updates = {};
        lugarSnap.forEach(child => {
          updates[`lugares_descubre/${child.key}/approved`] = false;
          updates[`lugares_descubre/${child.key}/updatedAt`] = now;
        });
        await update(dbRef(database), updates);
      }
    }

    dispatch({ type: UPDATE_COMPANY, payload: { id, status: 'blocked' } });
  } catch (err) {
    console.error('[disapproveCompany]', err);
    throw err;
  }
};

// ── Products ──────────────────────────────────────────────────────────────────
export const fetchProducts = (companyId) => (dispatch) => {
  const { companyProductsRef } = firebase;
  dispatch({ type: FETCH_PRODUCTS, payload: null });
  off(companyProductsRef(companyId));
  onValue(companyProductsRef(companyId), (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const list = Object.keys(data).map(k => ({ id: k, ...data[k] }));
      dispatch({ type: FETCH_PRODUCTS_SUCCESS, payload: { companyId, products: list } });
    } else {
      dispatch({ type: FETCH_PRODUCTS_SUCCESS, payload: { companyId, products: [] } });
    }
  }, (err) => {
    dispatch({ type: FETCH_PRODUCTS_FAILED, payload: err.message });
  });
};

export const stopFetchProducts = (companyId) => () => {
  const { companyProductsRef } = firebase;
  off(companyProductsRef(companyId));
};

export const createProduct = (companyId, productData, imageUris) => async (dispatch) => {
  try {
    const { companyProductsRef, productImageRef, singleCompanyRef } = firebase;
    const newRef = push(companyProductsRef(companyId));
    const id = newRef.key;

    const imageUrls = [];
    for (let i = 0; i < (imageUris || []).length; i++) {
      if (imageUris[i]) {
        const url = await uploadImage(productImageRef(companyId, id, i), imageUris[i]);
        imageUrls.push(url);
      }
    }

    const product = {
      ...productData,
      id,
      companyId,
      images: imageUrls,
      active: true,
      createdAt: Date.now(),
    };
    await set(newRef, product);
    // Increment product count on company
    const snap = await get(singleCompanyRef(companyId));
    if (snap.val()) {
      const count = (snap.val().products || 0) + 1;
      await update(singleCompanyRef(companyId), { products: count });
    }
    // onValue listener in fetchProducts will update the state automatically
    return { success: true, id };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const updateProduct = (companyId, productId, productData) => async (dispatch) => {
  try {
    const { singleProductRef } = firebase;
    const updates = { ...productData, updatedAt: Date.now() };
    await update(singleProductRef(companyId, productId), updates);
    dispatch({ type: UPDATE_PRODUCT, payload: { id: productId, companyId, ...updates } });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const deleteProduct = (companyId, productId) => async (dispatch) => {
  try {
    const { singleProductRef, singleCompanyRef } = firebase;
    await remove(singleProductRef(companyId, productId));
    const snap = await get(singleCompanyRef(companyId));
    if (snap.val()) {
      const count = Math.max(0, (snap.val().products || 1) - 1);
      await update(singleCompanyRef(companyId), { products: count });
    }
    dispatch({ type: DELETE_PRODUCT, payload: { companyId, productId } });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── States / Stories ──────────────────────────────────────────────────────────
export const fetchUserCompanyStates = (companyId) => (dispatch) => {
  const { userCompanyStatesRef } = firebase;
  const uid = store.getState().auth.profile?.uid;
  if (!uid) return;
  dispatch({ type: FETCH_COMPANY_STATES, payload: null });
  off(userCompanyStatesRef(uid));
  onValue(userCompanyStatesRef(uid), (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const list = Object.keys(data).map(k => ({ id: k, ...data[k] }));
      dispatch({ type: FETCH_COMPANY_STATES_SUCCESS, payload: list });
    } else {
      dispatch({ type: FETCH_COMPANY_STATES_SUCCESS, payload: [] });
    }
  }, (err) => {
    dispatch({ type: FETCH_COMPANY_STATES_FAILED, payload: err.message });
  });
};

export const createCompanyState = (stateData, mediaUri) => async (dispatch) => {
  try {
    const uid = store.getState().auth.profile?.uid;
    const { companyStatesRef, stateMediaRef } = firebase;
    const newRef = push(companyStatesRef);
    const id = newRef.key;

    let mediaUrl = null;
    if (mediaUri) {
      mediaUrl = await uploadImage(stateMediaRef(stateData.companyId, id), mediaUri);
    }

    const expiresAt = Date.now() + 24 * 60 * 60 * 1000; // 24h default
    const state = {
      ...stateData,
      id,
      ownerId: uid,
      mediaUrl,
      status: 'active',
      createdAt: Date.now(),
      expiresAt,
      views: 0,
    };
    await set(newRef, state);
    dispatch({ type: ADD_COMPANY_STATE, payload: state });
    return { success: true, id };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const deleteCompanyState = (stateId) => async () => {
  try {
    const { singleStateRef } = firebase;
    await remove(singleStateRef(stateId));
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const duplicateProduct = (companyId, productId) => async (dispatch) => {
  try {
    const { companyProductsRef, singleProductRef, singleCompanyRef } = firebase;
    const snap = await get(singleProductRef(companyId, productId));
    if (!snap.val()) return { success: false };
    const original = snap.val();
    const newRef = push(companyProductsRef(companyId));
    const id = newRef.key;
    const copy = { ...original, id, name: (original.name || 'Producto') + ' (copia)', createdAt: Date.now() };
    await set(newRef, copy);
    const cSnap = await get(singleCompanyRef(companyId));
    if (cSnap.val()) {
      await update(singleCompanyRef(companyId), { products: (cSnap.val().products || 0) + 1 });
    }
    // onValue listener in fetchProducts will update the state automatically
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const fetchAllActiveStates = () => (dispatch) => {
  const { allActiveStatesRef } = firebase;
  dispatch({ type: FETCH_COMPANY_STATES, payload: null });
  off(allActiveStatesRef);
  onValue(allActiveStatesRef, (snapshot) => {
    const now = Date.now();
    if (snapshot.val()) {
      const data = snapshot.val();
      const list = Object.keys(data)
        .map(k => ({ id: k, ...data[k] }))
        .filter(s => !s.expiresAt || s.expiresAt > now);
      dispatch({ type: FETCH_COMPANY_STATES_SUCCESS, payload: list });
    } else {
      dispatch({ type: FETCH_COMPANY_STATES_SUCCESS, payload: [] });
    }
  }, (err) => {
    dispatch({ type: FETCH_COMPANY_STATES_FAILED, payload: err.message });
  });
};

export const stopFetchAllActiveStates = () => () => {
  const { allActiveStatesRef } = firebase;
  off(allActiveStatesRef);
};
